import { useCallback, useEffect, useState } from 'react';
import { useMountedState } from 'react-use';
import { connect, useDispatch } from 'react-redux';
import { ARRAY_ERROR } from 'final-form';
import { FormSpy } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import type { Dispatch } from 'redux';

import { IRequirementTemplateSelectOption } from 'CorePackage/AccountModule/interfaces/IRequirementTemplateSelectOption';
import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import { getAccountKnoOrganizationsWithDistrictSelectOptions } from 'CorePackage/AccountModule/selectors/accountSelectors';
import { IKnmSupervisionTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmSupervisionTypeSelectOption';
import { deleteObjectProperties } from 'CorePackage/MainModule/common/deleteObjectProperties';
import { KNM_REQUIREMENTS } from 'CorePackage/MainModule/routes/routes';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IKnmErpForm } from 'KnmErpPackage/MainModule/interfaces/IKnmErpForm';
import { knmSetLoadingFlags } from 'KnmPackage/MainModule/actions/knmActions';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { REQUIREMENTS_TITLE_BLOCK_ID, getRequirementBlockId } from 'KnmPackage/MainModule/constants/fieldsElementsIds';
import { KnmLoadingFlagsEnum } from 'KnmPackage/MainModule/enums/KnmLoadingFlagsEnum';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { getKnmFormRef, getKnmSupervisionTypesSelectOptions } from 'KnmPackage/MainModule/selectors/knmSelectors';
import knmSharedStyles from 'KnmPackage/MainModule/styles/KnmShared.module.scss';
import { useRequirementTemplate } from 'PersonalAccountPackage/UserRequirementsModule/hooks/useRequirementTemplate';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Section } from 'UIPackage/FormModule/components/Section';
import { Row } from 'UIPackage/FormModule/components/Row';
import { KnmErpAddTemplatesModal } from 'KnmErpPackage/MainModule/components/KnmErpAddTemplatesModal';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { useKnmSupervisionTypes } from 'CorePackage/CatalogsModule/hooks/useKnmSupervisionTypes';

import { IKnmErpFormRequirement } from '../interfaces/IKnmErpFormRequirement';
import { KnmErpRequirement } from './KnmErpRequirement';

const COLLAPSE_TITLE_BASE = 'Обязательное требование';

interface IKnmErpRequirementsProps {
  formRequirements: IKnmErpForm['requirements'];
  watchOnly: boolean;
  error: string;
}

interface IKnmErpRequirementsStoreProps {
  formRef: IKnmFormRef | undefined;
  knoOrganizationsSelectOptions: IUserOrganisationSelectOption[];
  knmSupervisionTypesSelectOptions: IKnmSupervisionTypeSelectOption[] | undefined;
}

const KnmErpRequirementsWithoutStore = ({
  formRequirements,
  watchOnly,
  error,
  formRef,
  knmSupervisionTypesSelectOptions,
  knoOrganizationsSelectOptions,
}: IKnmErpRequirementsProps & IKnmErpRequirementsStoreProps) => {
  const isMounted = useMountedState();
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const dispatch = useDispatch<Dispatch<RootActions>>();
  const setRequirementsLoadingFlag = useCallback(
    (value: boolean) => dispatch(knmSetLoadingFlags(KnmLoadingFlagsEnum.REQUIREMENTS, value)),
    [dispatch],
  );

  const [isRequirementsModalVisible, setIsRequirementsModalVisible] = useState(false);

  const openRequirementsModal = useCallback(() => {
    !watchOnly && setIsRequirementsModalVisible(true);
  }, [watchOnly]);

  const closeRequirementsModal = useCallback(() => {
    !watchOnly && setIsRequirementsModalVisible(false);
  }, [watchOnly]);

  const { isLoading: isGetRequirementTemplateLoading, getRequirementTemplate } = useRequirementTemplate();

  useEffect(() => {
    setRequirementsLoadingFlag(isGetRequirementTemplateLoading);
  }, [isGetRequirementTemplateLoading, setRequirementsLoadingFlag]);

  const { supervisionTypesSelectOptions, refresh } = useKnmSupervisionTypes();

  const addKnmRequirement = useCallback(
    async (template: IRequirementTemplateSelectOption) => {
      if (!watchOnly) {
        const getRequirementTemplateResponse = await getRequirementTemplate(template.id);

        if (getRequirementTemplateResponse?.success && isMounted()) {
          const newRequirementTemplate = getRequirementTemplateResponse.data;
          const knoOrganizationSelectOption = knoOrganizationsSelectOptions?.find(
            option => option.id === template.knoOrganizationId,
          );
          const knoOrganization =
            knoOrganizationSelectOption && deleteObjectProperties(knoOrganizationSelectOption, ['label', 'value']);

          if (!knmSupervisionTypesSelectOptions) refresh();

          const supervisionTypeOption = knmSupervisionTypesSelectOptions
            ? knmSupervisionTypesSelectOptions?.find(option => option.id === template.supervisionType)
            : supervisionTypesSelectOptions?.find(option => option.id === template.supervisionType);

          const supervisionType =
            supervisionTypeOption && deleteObjectProperties(supervisionTypeOption, ['label', 'value']);

          const newRequirement: IKnmErpFormRequirement = {
            name: newRequirementTemplate?.name || null,
            template: newRequirementTemplate || null,
            documents: newRequirementTemplate?.requirements || undefined,
            knoOrganization: knoOrganization || null,
            supervisionType: supervisionType || null,
            guid: null,
            isNew: true,
            listId: uuidv4(),
          };

          if (push) {
            push('requirements', newRequirement);
          }
        }
      }
    },
    [
      getRequirementTemplate,
      isMounted,
      knmSupervisionTypesSelectOptions,
      knoOrganizationsSelectOptions,
      push,
      watchOnly,
    ],
  );

  const handleRequirementsModalApply = useCallback(
    (template: IRequirementTemplateSelectOption) => addKnmRequirement(template),
    [addKnmRequirement],
  );

  const makeRemoveRequirementHandler = (index: number) => () => !watchOnly && remove && remove('requirements', index);

  return (
    <Section
      title="Подлежащие проверке обязательные требования"
      id={KNM_REQUIREMENTS}
      titleId={REQUIREMENTS_TITLE_BLOCK_ID}
      customButton={
        <Button
          className="shared-btn-add"
          text="Добавить"
          type="button"
          variant="link"
          color="primary"
          disabled={watchOnly || Boolean(error)}
          onClick={openRequirementsModal}
        />
      }
      type="list"
    >
      <Row>
        {error && <p className={knmSharedStyles.SectionErrorNotification}>{error}</p>}
        <FormSpy subscription={{ errors: true, touched: true }}>
          {props => {
            const requirementsFieldError = props.errors?.requirements?.[ARRAY_ERROR];
            const isRequirementsFieldTouched = props.touched?.requirements;

            if (!error && requirementsFieldError && isRequirementsFieldTouched) {
              return <p className={knmSharedStyles.SectionErrorNotification}>{requirementsFieldError}</p>;
            }

            return null;
          }}
        </FormSpy>
      </Row>

      {!error && (
        <FieldArray name="requirements">
          {({ fields }) => {
            if (!fields.length) {
              return null;
            }

            return (
              <Row>
                {fields.map((name, index) => {
                  const fieldValue: IKnmErpFormRequirement = fields.value?.[index];
                  const id = getRequirementBlockId(fieldValue.listId);

                  return (
                    <Row isListItem margin="medium" key={id}>
                      <KnmCollapse
                        theme="fields"
                        title={fieldValue?.isNew ? COLLAPSE_TITLE_BASE : `${COLLAPSE_TITLE_BASE} №${index + 1}`}
                        subtitle={fieldValue?.template?.name}
                        isNew={fieldValue?.isNew}
                        openByDefault={fieldValue?.isNew}
                        onClose={makeRemoveRequirementHandler(index)}
                      >
                        <KnmErpRequirement remove={remove} fieldName={name} data={fieldValue} watchOnly={watchOnly} />
                      </KnmCollapse>
                    </Row>
                  );
                })}
              </Row>
            );
          }}
        </FieldArray>
      )}

      <KnmErpAddTemplatesModal
        visible={isRequirementsModalVisible}
        formRequirements={formRequirements}
        onApply={handleRequirementsModalApply}
        onClose={closeRequirementsModal}
      />
    </Section>
  );
};

const mapStateToProps = (state: IRootState): IKnmErpRequirementsStoreProps => ({
  formRef: getKnmFormRef(state),
  knoOrganizationsSelectOptions: getAccountKnoOrganizationsWithDistrictSelectOptions(state),
  knmSupervisionTypesSelectOptions: getKnmSupervisionTypesSelectOptions(state),
});

export const KnmErpRequirements = connect(mapStateToProps)(KnmErpRequirementsWithoutStore);
